import React, { Fragment, useEffect, useRef, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import DataTable from "react-data-table-component";
import { Container, Row, Col, Card, CardBody } from "reactstrap";
import { Breadcrumbs, Btn, P } from "../../AbstractElements";
import { getApiBase } from "../../helpers/apiBase";

/** Ответ POST /quest/list: новый формат { items, total } или старый — массив квестов. */
const normalizeQuestRow = (raw) => {
  if (!raw || typeof raw !== "object") {
    return null;
  }
  return {
    id: raw.id,
    name: raw.name ?? "",
    category: raw.category ?? null,
    category_name:
      raw.category_name !== undefined && raw.category_name !== ""
        ? raw.category_name
        : "",
    image32: raw.image32 || raw.image || "",
  };
};

const tableCustomStyles = {
  table: {
    style: {
      width: "100%",
    },
  },
  responsiveWrapper: {
    style: {
      width: "100%",
    },
  },
};

const QuestList = () => {
  const [searchParams] = useSearchParams();
  const locationId = searchParams.get("location_id");

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const lastLoc = useRef(null);

  useEffect(() => {
    if (!locationId) {
      lastLoc.current = null;
      setRows([]);
      setTotal(0);
      return;
    }

    if (lastLoc.current !== null && lastLoc.current !== locationId) {
      lastLoc.current = locationId;
      setPage(1);
      return;
    }
    lastLoc.current = locationId;

    const load = async () => {
      const base = getApiBase();
      if (!base) {
        setError("Не задан REACT_APP_FW_API для запросов к API.");
        return;
      }
      setLoading(true);
      setError("");
      try {
        const token = localStorage.getItem("token");
        const headers = {
          "Content-Type": "application/json",
        };
        if (token) {
          headers.Authorization = `Bearer ${token}`;
        }
        const res = await fetch(`${base}/v1/quest/list`, {
          method: "POST",
          headers,
          body: JSON.stringify({
            company_id: Number(locationId),
            page,
            "page-size": perPage,
          }),
        });
        const data = await res.json().catch(() => ({}));
        if (res.status === 401) {
          setError("Вы не авторизованы или сессия истекла. Перезайдите в cpai.");
          return;
        }
        if (!res.ok) {
          setError(
            data && data.message
              ? data.message
              : `Ошибка загрузки квестов (код ${res.status}).`
          );
          return;
        }
        let list = [];
        let totalCount = 0;

        if (data && Array.isArray(data.items)) {
          list = data.items;
          totalCount =
            typeof data.total === "number" ? data.total : list.length;
        } else if (Array.isArray(data)) {
          list = data;
          totalCount = data.length;
        }

        setRows(list.map(normalizeQuestRow).filter(Boolean));
        setTotal(totalCount);
      } catch (e) {
        setError("Ошибка сети при загрузке списка квестов.");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [locationId, page, perPage]);

  const columns = useMemo(
    () => [
      {
        name: "id",
        selector: (row) => row.id,
        sortable: true,
        grow: 1,
        minWidth: "64px",
      },
      {
        name: "image32",
        cell: (row) => (
          <img
            src={row.image32 || ""}
            alt=""
            className="rounded-circle img-30"
            style={{ width: 32, height: 32, objectFit: "cover" }}
          />
        ),
        grow: 1,
        minWidth: "72px",
      },
      {
        name: "name",
        cell: (row) => (
          <div
            className="text-truncate w-100"
            style={{ minWidth: 0 }}
            title={row.name || ""}
          >
            {row.name}
          </div>
        ),
        selector: (row) => row.name,
        sortable: true,
        grow: 1,
        minWidth: "100px",
      },
      {
        name: "category",
        selector: (row) =>
          row.category_name !== undefined && row.category_name !== ""
            ? row.category_name
            : row.category ?? "",
        sortable: true,
        grow: 1,
        minWidth: "80px",
      },
      {
        name: "",
        grow: 1,
        minWidth: "140px",
        cell: () => (
          <Btn
            attrBtn={{
              color: "primary",
              outline: true,
              size: "sm",
              type: "button",
              className: "text-nowrap",
              style: { whiteSpace: "nowrap" },
              onClick: (e) => e.preventDefault(),
            }}
          >
            Редактировать
          </Btn>
        ),
        ignoreRowClick: true,
      },
    ],
    []
  );

  const handlePageChange = (p) => {
    setPage(p);
  };

  const handlePerRowsChange = (newPerPage, p) => {
    setPerPage(newPerPage);
    setPage(p);
  };

  return (
    <Fragment>
      <Breadcrumbs mainTitle="Квесты" parent="Локация" title="Список квестов" />
      <Container fluid={true}>
        <Row>
          <Col sm="12">
            <Card>
              <CardBody className="pt-0">
                {!locationId && (
                  <P className="text-muted mb-0">
                    Укажите в адресе параметр{" "}
                    <code className="user-select-all">?location_id=…</code>
                  </P>
                )}
                {locationId && error && (
                  <P className="text-danger mb-2">{error}</P>
                )}
                {locationId && !error && loading && (
                  <P className="text-muted mb-2">Загрузка…</P>
                )}
                {locationId && (
                  <DataTable
                    key={locationId}
                    className="data-tables theme-scrollbar"
                    customStyles={tableCustomStyles}
                    columns={columns}
                    data={rows}
                    striped
                    center
                    pagination
                    paginationServer
                    paginationTotalRows={total}
                    paginationPerPage={perPage}
                    paginationRowsPerPageOptions={[10, 20, 50, 100]}
                    onChangePage={handlePageChange}
                    onChangeRowsPerPage={handlePerRowsChange}
                    progressPending={loading}
                    persistTableHead
                  />
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </Fragment>
  );
};

export default QuestList;
